import { useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useCollection } from "../../hooks/useCollection";
import ImageUploader from "../../components/admin/ImageUploader";

const emptyForm = {
  title: "",
  summary: "",
  imageUrl: "",
  link: "",
  order: 0,
};

export default function DocumentsAdmin() {
  const { items, loading } = useCollection("documents", "order", "asc");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const reset = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      summary: item.summary || "",
      imageUrl: item.imageUrl || "",
      link: item.link || "",
      order: item.order ?? 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
        updatedAt: serverTimestamp(),
      };
      if (editingId) {
        await updateDoc(doc(db, "documents", editingId), payload);
      } else {
        await addDoc(collection(db, "documents"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      reset();
    } catch (err) {
      alert(`บันทึกไม่สำเร็จ: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`ลบ "${item.title}"?`)) return;
    await deleteDoc(doc(db, "documents", item.id));
  };

  return (
    <div>
      <h2 className="mb-4">จัดการ Documents</h2>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            {editingId ? "แก้ไขเอกสาร" : "เพิ่มเอกสารใหม่"}
          </h5>
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">หัวข้อเอกสาร</label>
                <input
                  className="form-control"
                  value={form.title}
                  onChange={update("title")}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">ลำดับ</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.order}
                  onChange={update("order")}
                />
              </div>
              <div className="col-12">
                <label className="form-label">คำอธิบาย</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.summary}
                  onChange={update("summary")}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">ลิงก์อ่าน/ดาวน์โหลด</label>
                <input
                  type="url"
                  className="form-control"
                  value={form.link}
                  onChange={update("link")}
                  placeholder="https://..."
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">รูปภาพปก</label>
                <ImageUploader
                  value={form.imageUrl}
                  onChange={(url) => setForm({ ...form, imageUrl: url })}
                />
              </div>
            </div>
            <div className="mt-3 d-flex gap-2">
              <button className="btn btn-primary" disabled={saving}>
                {saving ? "กำลังบันทึก..." : editingId ? "บันทึก" : "เพิ่ม"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={reset}
                >
                  ยกเลิก
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <h5 className="card-title mb-3">รายการ ({items.length})</h5>
          {loading ? (
            <div>กำลังโหลด...</div>
          ) : items.length === 0 ? (
            <div className="text-muted">ยังไม่มีเอกสาร</div>
          ) : (
            <table className="table align-middle">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>รูป</th>
                  <th>หัวข้อ</th>
                  <th style={{ width: 80 }}>ลำดับ</th>
                  <th style={{ width: 180 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.imageUrl && (
                        <img
                          src={item.imageUrl}
                          alt=""
                          style={{ width: 60, height: 45, objectFit: "cover" }}
                          className="rounded"
                        />
                      )}
                    </td>
                    <td>
                      <div className="fw-semibold">{item.title}</div>
                      <div className="small text-muted">{item.summary}</div>
                    </td>
                    <td>{item.order ?? 0}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => handleEdit(item)}
                      >
                        แก้ไข
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(item)}
                      >
                        ลบ
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
